import * as React from 'react';
import { getGetResolvedBrandingQueryKey, useGetResolvedBranding } from '@cdf/api-client-react';
import type { DesignSystemCssVars } from '@cdf/api-client-react';

import { useTenantEntity } from '@/components/access/tenant-entity-provider';

const STYLE_ID = 'tenant-branding';
const NAME_RE = /^--[a-z0-9-]{1,64}$/;
const UNSAFE_VALUE_RE = /[;{}<>\\]|url\(|expression\(|@import/i;

/** Serialises validated variables; anything unexpected is dropped rather than injected. */
export function brandingCss(light: DesignSystemCssVars, dark: DesignSystemCssVars): string {
  const block = (selector: string, vars: DesignSystemCssVars) => {
    const lines = Object.entries(vars)
      .filter(([name, value]) => NAME_RE.test(name) && typeof value === 'string' && value.length <= 200 && !UNSAFE_VALUE_RE.test(value))
      .map(([name, value]) => `  ${name}: ${value};`);
    return lines.length ? `${selector} {\n${lines.join('\n')}\n}` : '';
  };
  return [block(':root', light), block('.dark', dark)].filter(Boolean).join('\n');
}

function applyBranding(css: string | null): void {
  const existing = document.getElementById(STYLE_ID);
  if (!css) {
    existing?.remove();
    return;
  }
  const element = existing ?? Object.assign(document.createElement('style'), { id: STYLE_ID });
  element.textContent = css;
  if (!existing) document.head.appendChild(element);
}

/**
 * Applies the stored design system chosen for the active entity (or tenant).
 * The built-in Solution Studio tokens in index.css remain the fallback while
 * loading, on error and when no stored design system is active.
 */
export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { selected, status } = useTenantEntity();
  const ready = status === 'ready' && Boolean(selected);
  const branding = useGetResolvedBranding({
    query: {
      queryKey: [...getGetResolvedBrandingQueryKey(), selected?.tenantId ?? null, selected?.entityId ?? null],
      enabled: ready,
      staleTime: 5 * 60_000,
      retry: false
    }
  });

  const data = branding.data;
  React.useEffect(() => {
    if (!ready || !data || data.source === 'platform') {
      applyBranding(null);
      return;
    }
    applyBranding(brandingCss(data.light, data.dark));
  }, [ready, data]);

  React.useEffect(() => () => applyBranding(null), []);

  return <>{children}</>;
}
